import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { WORK_STATUSES, toLocalInput, fromLocalInput, rupee } from "../../lib/customers";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";

const KEYS = ["service_name", "category", "description", "application_no", "registration_no", "portal_login_id", "registered_email",
  "registered_mobile", "application_date", "last_date", "status", "govt_fee", "service_charge", "paid_amount", "assigned_operator", "next_followup_at", "notes"];
const NUM = ["govt_fee", "service_charge", "paid_amount"];

const F = ({ label, k, form, set, type = "text", span }) => (
  <div className={span ? "sm:col-span-2" : ""}>
    <Label className="text-xs">{label}</Label>
    <Input type={type} value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`work-${k}`} />
  </div>
);

export const WorkForm = ({ cid, work, onClose, onSaved }) => {
  const [form, setForm] = useState(() => {
    const f = Object.fromEntries(KEYS.map((k) => [k, work?.[k] ?? (NUM.includes(k) ? 0 : k === "status" ? "Pending" : "")]));
    f.next_followup_at = toLocalInput(work?.next_followup_at);
    return f;
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });
  const p = { form, set };
  const total = Number(form.govt_fee || 0) + Number(form.service_charge || 0);
  const due = Math.max(total - Number(form.paid_amount || 0), 0);

  const save = async () => {
    if (!form.service_name.trim()) { toast.error("Service Name zaroori hai"); return; }
    setSaving(true);
    try {
      const body = { ...form, next_followup_at: fromLocalInput(form.next_followup_at), ...Object.fromEntries(NUM.map((k) => [k, Number(form[k] || 0)])) };
      if (work?.id) await api.put(`/customers/${cid}/works/${work.id}`, body); else await api.post(`/customers/${cid}/works`, body);
      toast.success("Work save ho gaya"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="work-dialog">
        <DialogHeader><DialogTitle>{work?.id ? `Edit ${work.work_code}` : "Add New Work"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <F label="Service Name *" k="service_name" {...p} />
          <F label="Service Category" k="category" {...p} />
          <div className="sm:col-span-2"><Label className="text-xs">Service Description</Label><Textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="work-description" /></div>
          <F label="Application Number" k="application_no" {...p} />
          <F label="Registration Number" k="registration_no" {...p} />
          <F label="Portal Login ID" k="portal_login_id" {...p} />
          <F label="Registered Email" k="registered_email" type="email" {...p} />
          <F label="Registered Mobile" k="registered_mobile" {...p} />
          <div>
            <Label className="text-xs">Work Status</Label>
            <select value={form.status} onChange={(e) => set("status", e.target.value)} className="w-full h-9 rounded-md border border-input bg-white px-2" data-testid="work-status">
              {WORK_STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <F label="Application Date" k="application_date" type="date" {...p} />
          <F label="Application Last Date" k="last_date" type="date" {...p} />
          <F label="Government Fee (₹)" k="govt_fee" type="number" {...p} />
          <F label="Ashish Point Service Charge (₹)" k="service_charge" type="number" {...p} />
          <F label="Paid Amount (₹)" k="paid_amount" type="number" {...p} />
          <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs" data-testid="work-money-summary">Total: <b>{rupee(total)}</b> · Due: <b className={due ? "text-red-600" : "text-green-700"}>{rupee(due)}</b></div>
          <F label="Assigned Operator" k="assigned_operator" {...p} />
          <F label="Next Follow-up Date" k="next_followup_at" type="datetime-local" {...p} />
          <div className="sm:col-span-2"><Label className="text-xs">Work Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="work-notes" /></div>
          <div className="sm:col-span-2 text-xs text-slate-500">Portal password yahan na likhein — save karne ke baad work card me "Credential Vault" (encrypted) use karein. OTP / UPI PIN / CVV kabhi save na karein.</div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="work-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="work-save-btn">{saving ? "Saving…" : "Save Work"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
